/**
 * Ряд розділів під шапкою — лише для облікового запису ArtemK.
 *
 * ОДИН РЯД НА ВСІ ЦІ РОЗДІЛИ. Спершу їх було два: «Оптимізація»
 * (Диспетчерська, Прогноз, Ціни) і «Фінанси» (Фінансовий результат, Бюджет).
 * 05.10 користувач вирішив звести їх в один ряд із п'яти кнопок: ходити між
 * прогнозом, цінами й грошима потрібно постійно, а два окремі ряди змушували
 * повертатися в сайдбар, щоб перескочити з одного набору в інший.
 *
 * Щоб положення ряду було ОДНАКОВЕ на всіх сторінках, компонент рендерить
 * каркас (DashboardLayout) одразу під шапкою, а не кожна сторінка в себе —
 * інакше п'ять різних верстак дали б п'ять різних відступів.
 *
 * Кнопки лише з іконками (`iconOnly`), підпис — у нативній підказці при
 * наведенні та в `aria-label`: той самий вигляд, що вкладки станції на SCADA.
 *
 * Для решти акаунтів і поза цими маршрутами компонент не рендерить нічого.
 */
import i18n from '@/i18n';
import { useEffect, useState, type ReactNode } from 'react';
import { pageMaxClass } from '@/utils/pageWidth';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Workflow, BarChart3, Coins, Wallet, LineChart, Landmark, TrendingUp, type LucideIcon } from 'lucide-react';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import { getStoredUser } from '@/hooks/useAuth';
import { hasArtemKView } from '@/utils/artemkView';

interface OptSection {
  key: string;
  label: string;
  Icon: LucideIcon;
  /**
   * Розділ, до якого маршрут НАЛЕЖИТЬ, але власної кнопки в ряду не має.
   * «Торговий графік» переїхав у перемикач «Цін» четвертою вкладкою, проте
   * ряд розділів на його сторінці має лишатись видимим — інакше з /trading
   * не було б ходу до Прогнозу чи Диспетчерської.
   */
  hidden?: boolean;
}

/**
 * П'ять кнопок ряду. Порядок: спершу гроші (там, де ряд жив як «Фінанси»),
 * далі три розділи, що переїхали з «Диспетчерської» — так дві кнопки, до яких
 * користувач уже звик, лишились на своїх місцях, а нові стали праворуч.
 *
 * «Команди диспетчера» (/sko) у ряду немає зовсім: у кабінеті ArtemK цього
 * розділу більше нема. Маршрут лишився робочим і доступним іншим акаунтам.
 */
export const SECTIONS: OptSection[] = [
  { key: '/financial/result', get label() { return i18n.t('ak_nav_financial_result'); }, Icon: Landmark },
  { key: '/budget', get label() { return i18n.t('ak_nav_budget'); }, Icon: Wallet },
  { key: '/dispatch/forecast-mms', get label() { return i18n.t('ak_nav_dispatch_room'); }, Icon: Workflow },
  { key: '/forecast', get label() { return i18n.t('ak_nav_forecast'); }, Icon: BarChart3 },
  { key: '/market/prices', get label() { return i18n.t('ak_nav_prices'); }, Icon: Coins },
  // 23.10: прогноз ціни РДН — поруч із «Цінами», як просив власник.
  { key: '/trading', get label() { return i18n.t('ak_nav_trading_schedule'); }, Icon: LineChart, hidden: true },
];

/**
 * Активний розділ за адресою. Підмаршрути (напр. /trading/dam) рахуються за
 * батьківським розділом; при збігу кількох перемагає найдовший ключ.
 */
const sectionOf = (pathname: string): string | null =>
  SECTIONS
    .filter((s) => pathname === s.key || pathname.startsWith(s.key + '/'))
    .sort((a, b) => b.key.length - a.key.length)[0]?.key ?? null;

/** Чи показується ряд на цій сторінці (сторінки підлаштовують під нього верстку). */
export function useOptSectionsVisible(): boolean {
  const { pathname } = useLocation();
  return hasArtemKView(getStoredUser()?.login) && sectionOf(pathname) !== null;
}

/**
 * Правий слот того самого рядка. Сторінка кладе туди свій елемент (напр.
 * погодний чіп на «Прогнозі») через портал — так він стоїть НА ОДНІЙ ЛІНІЇ з
 * кнопками, хоча живе у своїй сторінці. Через портал, а не пропи, бо ряд
 * малює каркас і сторінки про нього нічого не знають.
 */
export const OPT_TABS_SLOT_ID = 'opt-tabs-right-slot';

export function OptTabsRightSlot({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [node, setNode] = useState<HTMLElement | null>(null);
  // Вузол зʼявляється в DOM після монтування каркаса, тож шукаємо його в
  // ефекті й перемальовуємось; при зміні маршруту перевіряємо знову.
  useEffect(() => { setNode(document.getElementById(OPT_TABS_SLOT_ID)); }, [pathname]);
  return node ? createPortal(children, node) : null;
}

export default function OptSectionTabs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = sectionOf(pathname);
  if (!hasArtemKView(getStoredUser()?.login) || !active) return null;
  return (
    // Та сама колонка, що й вміст сторінок ряду: `px-4 md:px-6` + `max-w-[1400px]`
    // без центрування. Без max-width ряд розтягувався на всю ширину main, і
    // правий слот (погодний чіп на «Прогнозі») виїжджав за правий край крайньої
    // KPI-плитки замість рівняння по ній.
    <div className={`px-4 md:px-6 pt-4 flex items-center ${pageMaxClass(1400)}`}>
      <SegmentedTabs
        variant="outline"
        iconOnly
        items={SECTIONS.filter((s) => !s.hidden).map((s) => ({ key: s.key, label: s.label, Icon: s.Icon }))}
        activeKey={active}
        onSelect={(key) => { if (key !== active) navigate(key); }}
      />
      {/* Правий слот: сторінка може портувати сюди свій елемент. */}
      <div id={OPT_TABS_SLOT_ID} className="ml-auto flex items-center gap-3 pl-3" />
    </div>
  );
}
